import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { adminApi } from '../api';
import { toast } from 'sonner';
import { Check, ImageIcon, Loader2 } from 'lucide-react';

/**
 * Dialog to change the cover photo of a Gallery ("Lavoro").
 * Loads all subgalleries + their photos, lets admin pick one → sets it as cover.
 */
const ChangeGalleryCoverDialog = ({ open, onOpenChange, gallery, onSaved }) => {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState('');

  useEffect(() => {
    if (!open || !gallery?.id) return;
    setSelected(gallery.cover_url || '');
    setLoading(true);
    (async () => {
      try {
        const subs = await adminApi.listSubgalleries(gallery.id);
        const all = [];
        for (const s of subs) {
          const detail = await adminApi.getSubgallery(s.id);
          (detail.photos || []).forEach(p => all.push({ ...p, sub_title: s.title }));
        }
        setPhotos(all);
      } catch (err) {
        console.error('ChangeGalleryCoverDialog: failed to load photos', err);
        toast.error('Errore nel caricamento delle foto');
      } finally { setLoading(false); }
    })();
  }, [open, gallery]);

  const save = async () => {
    if (!selected) return toast.error('Seleziona una foto');
    const photo = photos.find(p => p.url === selected);
    if (!photo) return toast.error('Foto non trovata');
    setSaving(true);
    try {
      await adminApi.setGalleryCover(gallery.id, photo.id);
      toast.success('Copertina del lavoro aggiornata');
      onOpenChange(false);
      onSaved && await onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Errore salvataggio');
    } finally { setSaving(false); }
  };

  if (!gallery) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Cambia copertina — {gallery.title}</DialogTitle>
          <DialogDescription>Scegli una foto dalle sotto-gallerie di questo lavoro come nuova copertina.</DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto mt-2 min-h-[200px]">
          {loading ? (
            <div className="grid place-items-center py-16 text-slate-400">
              <Loader2 className="w-6 h-6 animate-spin mb-2" />
              <span className="text-sm">Caricamento foto…</span>
            </div>
          ) : photos.length === 0 ? (
            <div className="grid place-items-center py-16 text-slate-400 text-center">
              <ImageIcon className="w-10 h-10 mb-2" />
              <span className="text-sm">Nessuna foto in questo lavoro. Carica prima delle foto in una sotto-galleria.</span>
            </div>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
              {photos.map((p) => {
                const isSel = selected === p.url;
                return (
                  <button
                    key={p.id}
                    type="button"
                    data-testid={`gallery-cover-photo-${p.id}`}
                    onClick={()=>setSelected(p.url)}
                    className={`relative aspect-square rounded-lg overflow-hidden border-2 transition ${isSel ? 'border-[#C9A961] ring-2 ring-[#C9A961]/50' : 'border-transparent hover:border-slate-300'}`}
                    title={p.sub_title}
                  >
                    <img src={p.url} alt={p.filename} className="w-full h-full object-cover" loading="lazy" />
                    {isSel && (
                      <div className="absolute inset-0 bg-[#C9A961]/20 grid place-items-center">
                        <div className="w-8 h-8 rounded-full bg-[#C9A961] text-black grid place-items-center shadow-lg">
                          <Check className="w-5 h-5" />
                        </div>
                      </div>
                    )}
                    <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/70 to-transparent px-1.5 py-1 text-[9px] text-white/90 truncate">{p.sub_title}</div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between gap-2 pt-3 border-t border-slate-100">
          <span className="text-xs text-slate-500">{photos.length} {photos.length === 1 ? 'foto disponibile' : 'foto disponibili'}</span>
          <div className="flex items-center gap-2">
            <button type="button" onClick={()=>onOpenChange(false)} className="px-4 py-2 text-sm text-slate-600 hover:text-slate-900">Annulla</button>
            <button data-testid="save-gallery-cover" disabled={!selected || saving} onClick={save} className="inline-flex items-center gap-2 bg-black hover:bg-[#C9A961] hover:text-black text-white text-sm font-semibold px-5 py-2.5 rounded-full transition-colors disabled:opacity-40">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              {saving ? 'Salvataggio…' : 'Imposta come copertina'}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ChangeGalleryCoverDialog;
